import { useLazyQuery } from "@apollo/client";
import {
  FilterValueMeta,
  FilterValueMetaType,
  FilterValueMetaInput,
} from "@towersystems/roam-common/lib/generated-types";
import { useEffect, useState, useCallback } from "react";
import {
  CacheRegistry,
  FilterMetadataContext,
} from "../../utilities/filter-metadata";
import { QUERY_UNDOCUMENTED_FILTER_METADATA } from "./constants";

export interface FilterMetadataControllerProps {
  children?: React.ReactNode;
}

export const FilterMetadataController = ({
  children,
}: FilterMetadataControllerProps) => {
  const [metaQuery, { loading, error, data }] = useLazyQuery<{
    undocumentedFilterMetadata: FilterValueMeta[];
  }>(QUERY_UNDOCUMENTED_FILTER_METADATA);

  useEffect(() => {
    if (data) {
      for (const i of data.undocumentedFilterMetadata) {
        addToCache(i.type, i.value, i.label);
      }
    }
  }, [data]);

  const [cache, setCache] = useState<CacheRegistry>({});

  const addToCache = useCallback(
    (type: FilterValueMetaType, id: string, value: string) => {
      setCache((c) => {
        return {
          ...c,
          [type]: {
            ...c[type],
            [id]: value,
          },
        };
      });
    },
    [setCache]
  );

  const getFromCache = useCallback(
    (type: FilterValueMetaType, id: string) => {
      return cache[type]?.[id];
    },
    [cache]
  );

  const replaceCache = useCallback(
    (cache: CacheRegistry) => {
      setCache(cache);
    },
    [setCache]
  );

  const queryMetadata = useCallback(
    async (input: FilterValueMetaInput) => {
      const r = {
        ...input,
        value: input.value.filter((i) => !cache[input.type]?.[i]),
      };
      if (r.value.length >= 1) {
        const res = await metaQuery({
          variables: {
            filters: input,
          },
        });
        return res.data?.undocumentedFilterMetadata || [];
      } else {
        return [];
      }
    },
    [cache]
  );

  const context = {
    cachedValues: cache,
    loading,
    error,
    addToCache,
    getFromCache,
    replaceCache,
    queryMetadata,
  };

  return (
    <FilterMetadataContext.Provider value={context}>
      {children}
    </FilterMetadataContext.Provider>
  );
};
